// SPDX-License-Identifier: SEE LICENSE
import type { StepStatus, TelemetryFrame } from './types';

export function statusColor(status: StepStatus | string): string {
  switch (status) {
    case 'passed':
      return '#5fd0a8';
    case 'concern':
      return '#f0a35e';
    case 'failed':
      return '#f0726c';
    case 'active':
      return '#7fe8c9';
    case 'skipped':
      return '#7b8794';
    default:
      return '#475569';
  }
}

export function statusLabel(status: StepStatus | string): string {
  switch (status) {
    case 'passed':
      return 'Passed';
    case 'concern':
      return 'Concern';
    case 'failed':
      return 'Failed';
    case 'active':
      return 'Running';
    case 'skipped':
      return 'Skipped';
    default:
      return 'Pending';
  }
}

export function overallColor(o: string): string {
  switch (o) {
    case 'pass':
      return '#5fd0a8';
    case 'concern':
      return '#f0a35e';
    case 'fail':
      return '#f0726c';
    default:
      return '#7fe8c9';
  }
}

export function relTime(ms: number): string {
  const d = Date.now() - ms;
  if (d < 1000) return 'just now';
  const s = Math.floor(d / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function fmtNum(v: unknown, digits = 1): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return '—';
  return v.toFixed(digits);
}

/** Pull a numeric series for a metric key from frames (for sparklines). */
export function seriesFor(frames: TelemetryFrame[], key: string): number[] {
  const out: number[] = [];
  for (const f of frames) {
    const v = f.metrics?.[key];
    if (typeof v === 'number' && Number.isFinite(v)) out.push(v);
  }
  return out;
}

export function lastMetric(frames: TelemetryFrame[], key: string): number | string | undefined {
  for (let i = frames.length - 1; i >= 0; i--) {
    const v = frames[i]?.metrics?.[key];
    if (typeof v === 'number' || typeof v === 'string') return v;
  }
  return undefined;
}

/** Latest array-valued metric (e.g. spectrum/grid) from frames. */
export function lastArray(frames: TelemetryFrame[], key: string): number[] | undefined {
  for (let i = frames.length - 1; i >= 0; i--) {
    const v = frames[i]?.metrics?.[key];
    if (Array.isArray(v)) return v as number[];
  }
  return undefined;
}

/** Latest string-valued metric from frames. */
export function lastString(frames: TelemetryFrame[], key: string): string | undefined {
  for (let i = frames.length - 1; i >= 0; i--) {
    const v = frames[i]?.metrics?.[key];
    if (typeof v === 'string') return v;
  }
  return undefined;
}

export function shortHash(hex: string | undefined, head = 8, tail = 6): string {
  if (!hex) return '—';
  if (hex.length <= head + tail + 1) return hex;
  return `${hex.slice(0, head)}…${hex.slice(-tail)}`;
}
